import { AppProps } from "next/app";
import "@/styles/globals.scss";
import React, { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { ThemeProvider } from "@/app/contexts/ThemeContext";
import Header from "@/app/components/UI/Header";
import Footer from "@/app/components/UI/Footer";
import useGoogleTagManager from "@/app/hooks/useGoogleTagManager";
import { AuthProvider } from "@/app/contexts/AuthContext";
import { useRouter } from "next/router";
import { BannerProvider } from "@/app/contexts/BannerContext";
import HeaderCMS from "@/app/components/UI/HeaderCMS";
import CustomLink from "@/app/components/UI/CustomLink";

const DynamicAlertModal = dynamic(
	() => import("@/app/components/UI/AlertModal"),
	{ ssr: false }
);
const DynamicConditionsModal = dynamic(
	() => import("@/app/components/UI/ConditionsModal"),
	{ ssr: false }
);

const cmsPath = "/cms";
const specialPaths = ["/programa-de-privacidade", cmsPath];

const needsStandardLayout = (path: string): boolean => {
	return !specialPaths.some(
		(specialPath) => path === specialPath || path.includes(specialPath)
	);
};

const App = ({ Component, pageProps }: AppProps) => {
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const router = useRouter();
	const { asPath } = router;
	const [newBanner, setNewBanner] = useState<boolean>(false);
	const [isConditionsModalOpen, setIsConditionsModalOpen] = useState(false);
	const [conditionsContent, setConditionsContent] = useState<string>("");

	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	// Dados que serão compartilhados pelo BannerProvider
	const bannerData = {
		contents: pageProps.contents || [],
		defenseTypes: pageProps.defenseTypes || [],
	};

	useGoogleTagManager();

	return (
		<>
			{needsStandardLayout(asPath) ? (
				<ThemeProvider>
					<BannerProvider {...bannerData}>
						<Header setIsAlertModalOpen={setIsAlertModalOpen} />
						<Component
							{...pageProps}
							setIsConditionsModalOpen={setIsConditionsModalOpen}
							setConditionsContent={setConditionsContent}
						/>
						<CustomLink
							className="wpp-button"
							href="https://wa.me/64996429826"
							target="_blank"
						>
							<img
								src="https://gabcomercio.azureedge.net/azul-agro/general/wpp.svg"
								alt="WhatsApp da Azul Agro"
								width={72}
								height={72}
							/>
						</CustomLink>
						{isConditionsModalOpen && (
							<DynamicConditionsModal
								setIsConditionsModalOpen={setIsConditionsModalOpen}
								content={conditionsContent}
							/>
						)}
						{isAlertModalOpen && (
							<DynamicAlertModal setIsAlertModalOpen={setIsAlertModalOpen} />
						)}
						<Footer />
					</BannerProvider>
				</ThemeProvider>
			) : asPath.includes(cmsPath) ? (
				<AuthProvider>
					{/* Renderiza o HeaderCMS somente quando estamos no cliente */}
					{isClient &&
						router.pathname !== cmsPath &&
						window?.location?.hostname !== "azulagromaquinas.com.br" && (
							<HeaderCMS setNewBanner={setNewBanner} />
						)}
					<Component
						newBanner={newBanner}
						setNewBanner={setNewBanner}
						{...pageProps}
					/>
				</AuthProvider>
			) : (
				<ThemeProvider>
					<Component {...pageProps} />
				</ThemeProvider>
			)}
		</>
	);
};

export default App;
